import _ from 'lodash';

type RequestFn<Args extends unknown[], Result extends { data?: unknown }> = (
  ...args: Args
) => Promise<Result>;

/**
 * Wraps a request function so that calls with identical arguments share one network request.
 *
 * A call made while an identical request is in flight, or within `reuseMs` after it resolved,
 * gets that request's result instead of sending another. Each caller receives its own deep copy
 * of `data`, so one caller mutating the response cannot affect another. A failed request is
 * dropped immediately, so the next call retries.
 *
 * Arguments are compared by their JSON serialization, which is also what goes on the wire.
 */
export function coalesceIdenticalRequests<
  Args extends unknown[],
  Result extends { data?: unknown },
>(fn: RequestFn<Args, Result>, { reuseMs }: { reuseMs: number }) {
  const pending = new Map<string, Promise<Result>>();

  return (...args: Args): Promise<Result> => {
    let key: string;
    try {
      key = JSON.stringify(args);
    } catch {
      return fn(...args);
    }

    let shared = pending.get(key);
    if (!shared) {
      shared = fn(...args);
      pending.set(key, shared);
      shared.then(
        () => {
          setTimeout(() => {
            if (pending.get(key) === shared) {
              pending.delete(key);
            }
          }, reuseMs);
        },
        () => {
          if (pending.get(key) === shared) {
            pending.delete(key);
          }
        }
      );
    }

    return shared.then((result) => ({
      ...result,
      data: _.cloneDeep(result.data),
    }));
  };
}
